import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, Observable, pipe, switchMap, tap } from 'rxjs';

export type StateDictionary<TEntity> = {
  entityList: string;
  listLoading: string;
  createLoading: string;
  selectedEntity: string;
  selectedEntityLoading: string;
  entityId: keyof TEntity;
  sortFn?: (a: TEntity, b: TEntity) => number;
};

/**
 * Creates a reusable getAll method for signal stores
 *
 * @param options Configuration for the getAll method
 * @returns An rxMethod that handles fetching all entities
 */
export const getAll = <TEntity, TParams = void>(options: {
  store: any;
  service: { getAll: (params?: TParams) => Observable<TEntity[]> };
  dictionary: StateDictionary<TEntity>;
  onErrorCallback?: (error: any) => void;
}) => {
  const { store, service, dictionary, onErrorCallback } = options;
  const { sortFn, listLoading, entityList } = dictionary;

  return rxMethod<TParams | undefined>(
    pipe(
      tap(() => {
        patchState(store, { [listLoading]: true } as any);
      }),
      switchMap(params =>
        service.getAll(params).pipe(
          tap(entities => {
            const sortedEntities = sortFn ? [...entities].sort(sortFn) : entities;

            patchState(store, {
              [entityList]: sortedEntities,
              [listLoading]: false,
            } as any);
          }),
          catchError((error: any) => {
            patchState(store, { [listLoading]: false } as any);
            if (onErrorCallback) {
              onErrorCallback(error);
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

export const getAllWithParams = <TEntity, TParams>(options: {
  store: any;
  service: { getAll: (params: TParams) => Observable<TEntity[]> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (entities: TEntity[]) => void;
  onErrorCallback?: () => void;
}) => {
  const { store, service, dictionary, onErrorCallback, onSuccessCallback } = options;
  const { sortFn, listLoading, entityList } = dictionary;

  return rxMethod<TParams>(
    pipe(
      tap(() => {
        patchState(store, { [listLoading]: true } as any);
      }),
      switchMap(params =>
        service.getAll(params).pipe(
          tap(entities => {
            const sortedEntities = sortFn ? [...entities].sort(sortFn) : entities;
            patchState(store, {
              [entityList]: sortedEntities,
              [listLoading]: false,
            } as any);

            if (onSuccessCallback) {
              onSuccessCallback(sortedEntities);
            }
          }),
          catchError(() => {
            patchState(store, { [listLoading]: false } as any);

            if (onErrorCallback) {
              onErrorCallback();
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

/**
 * Creates a reusable getOne method for signal stores
 *
 * @returns An rxMethod that handles fetching a specific entity by id
 */
export const getOne = <TEntity>(options: {
  store: any;
  service: { getOne: (id: string) => Observable<TEntity> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (entity: TEntity) => void;
  onErrorCallback?: (error: any) => void;
  beforeRequestCallback?: () => void;
}) => {
  const { store, service, dictionary, onSuccessCallback, onErrorCallback, beforeRequestCallback } = options;
  const { selectedEntity, selectedEntityLoading } = dictionary;

  return rxMethod<string>(
    pipe(
      tap(() => {
        patchState(store, { [selectedEntityLoading]: true } as any);
        if (beforeRequestCallback) {
          beforeRequestCallback();
        }
      }),
      switchMap(id =>
        service.getOne(id).pipe(
          tap(entity => {
            patchState(store, {
              [selectedEntity]: entity,
              [selectedEntityLoading]: false,
            } as any);
            if (onSuccessCallback) {
              onSuccessCallback(entity);
            }
          }),
          catchError((error: any) => {
            patchState(store, { [selectedEntityLoading]: false } as any);
            if (onErrorCallback) {
              onErrorCallback(error);
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

export const getOneWithParams = <TEntity, TParams>(options: {
  store: any;
  service: { getOne: (params: TParams) => Observable<TEntity> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (entities: TEntity) => void;
  onErrorCallback?: () => void;
}) => {
  const { store, service, dictionary, onErrorCallback, onSuccessCallback } = options;
  const { selectedEntity, selectedEntityLoading } = dictionary;

  return rxMethod<TParams>(
    pipe(
      tap(() => {
        patchState(store, { [selectedEntityLoading]: true } as any);
      }),
      switchMap(params =>
        service.getOne(params).pipe(
          tap(entity => {
            patchState(store, {
              [selectedEntity]: entity,
              [selectedEntityLoading]: false,
            } as any);

            if (onSuccessCallback) {
              onSuccessCallback(entity);
            }
          }),
          catchError(() => {
            patchState(store, { [selectedEntityLoading]: false } as any);
            if (onErrorCallback) {
              onErrorCallback();
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

/**
 * Creates a reusable addEntity method for signal stores
 *
 * @param options Configuration for the add method
 * @returns An rxMethod that handles creating a new entity
 */
export const addEntity = <TEntity, TCreateDto>(options: {
  store: any;
  service: { add: (item: TCreateDto) => Observable<TEntity> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (entity: TEntity) => void;
  onErrorCallback?: (error: any) => void;
}) => {
  const { store, service, dictionary, onSuccessCallback, onErrorCallback } = options;
  const { sortFn, createLoading, entityList, listLoading, selectedEntity } = dictionary;

  return rxMethod<TCreateDto>(
    pipe(
      tap(() => {
        patchState(store, {
          [listLoading]: true,
          [createLoading]: true,
        } as any);
      }),
      switchMap(dto =>
        service.add(dto).pipe(
          tap(newEntity => {
            // Get current collection and add the new entity
            const currentCollection = store[entityList]() || [];
            const updatedCollection = sortFn ? [...currentCollection, newEntity].sort(sortFn) : [...currentCollection, newEntity];

            patchState(store, {
              [entityList]: updatedCollection,
              [selectedEntity]: newEntity,
              [listLoading]: false,
              [createLoading]: false,
            } as any);

            if (onSuccessCallback) {
              onSuccessCallback(newEntity);
            }
          }),
          catchError((error: any) => {
            patchState(store, {
              [listLoading]: false,
              [createLoading]: false,
            } as any);
            if (onErrorCallback) {
              onErrorCallback(error);
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

/**
 * Creates a reusable updateEntity method for signal stores
 *
 * @param options Configuration for the update method
 * @returns An rxMethod that handles updating an entity
 */
export const updateEntity = <TEntity, TUpdateDto>(options: {
  store: any;
  service: { update: (id: string, item: TUpdateDto) => Observable<TEntity> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (entity: TEntity) => void;
  onErrorCallback?: (error: any) => void;
}) => {
  const { store, service, dictionary, onSuccessCallback, onErrorCallback } = options;
  const { sortFn, entityList, listLoading, selectedEntityLoading, entityId, selectedEntity } = dictionary;

  return rxMethod<{ id: string; dto: TUpdateDto }>(
    pipe(
      tap(() => {
        patchState(store, {
          [listLoading]: true,
          [selectedEntityLoading]: true,
        } as any);
      }),
      switchMap(({ id, dto }) =>
        service.update(id, dto).pipe(
          tap(updatedEntity => {
            // Replace the entity in the collection
            const currentCollection = store[entityList]() || [];
            const updatedCollection = currentCollection.map((entity: any) =>
              String(entity[entityId as string]) === id ? updatedEntity : entity,
            );

            const sortedCollection = sortFn ? [...updatedCollection].sort(sortFn) : updatedCollection;

            patchState(store, {
              [entityList]: sortedCollection,
              [selectedEntity]: updatedEntity,
              [selectedEntityLoading]: false,
              [listLoading]: false,
            } as any);

            if (onSuccessCallback) {
              onSuccessCallback(updatedEntity);
            }
          }),
          catchError((error: any) => {
            patchState(store, {
              [listLoading]: false,
              [selectedEntityLoading]: false,
            } as any);
            if (onErrorCallback) {
              onErrorCallback(error);
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};

/**
 * Creates a reusable deleteEntity method for signal stores
 *
 * @param options Configuration for the delete method
 * @returns An rxMethod that handles deleting an entity
 */
export const deleteEntity = <TEntity>(options: {
  store: any;
  service: { delete: (id: string) => Observable<void> };
  dictionary: StateDictionary<TEntity>;
  onSuccessCallback?: (id: string) => void;
  onErrorCallback?: (error: any) => void;
}) => {
  const { store, service, dictionary, onSuccessCallback, onErrorCallback } = options;
  const { entityList, entityId, selectedEntity, selectedEntityLoading, listLoading } = dictionary;

  return rxMethod<string>(
    pipe(
      tap(() => {
        patchState(store, {
          [selectedEntityLoading]: true,
          [listLoading]: true,
        });
      }),
      switchMap(id =>
        service.delete(id).pipe(
          tap(() => {
            // Filter out the deleted entity
            const currentCollection = store[entityList]() || [];
            const filteredCollection = currentCollection.filter((entity: any) => String(entity[entityId as string]) !== id);

            patchState(store, {
              [entityList]: filteredCollection,
              [selectedEntity]: null,
              [selectedEntityLoading]: false,
              [listLoading]: false,
            });

            if (onSuccessCallback) {
              onSuccessCallback(id);
            }
          }),
          catchError((error: any) => {
            patchState(store, {
              [selectedEntityLoading]: false,
              [listLoading]: false,
            });
            if (onErrorCallback) {
              onErrorCallback(error);
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );
};
